import { type FieldDef } from '../types/dynamic-form.types';

/**
 * The kinds that compose their OWN accessible name — a `<Label htmlFor>` beside
 * one of these either competes with the name it already has or points at a
 * `div`, which names nothing.
 */
const SELF_NAMING_KINDS: ReadonlySet<FieldDef['kind']> = new Set<FieldDef['kind']>([
  'color',
  'toggle-group',
  // `ChoiceCards` puts the field's label on the `role="radiogroup"` as its
  // `aria-label`, and a group is not a labelable element — an `htmlFor` at it
  // names nothing while competing with the name the control already has.
  'radio-group',
  'key-value',
  'json',
  // A contenteditable is not a labelable element; the editor names itself.
  'markdown',
  // All three render SEVERAL named controls (an amount and a unit; a feet box, an
  // inches box and a unit; a preset chooser and a custom box), so one `htmlFor`
  // could name only one of them and would leave the rest anonymous. `percent`,
  // `money` and `country` are deliberately NOT here: each renders one value
  // control (beside an unnamed adornment, at most), and an `aria-label` on it
  // would beat the visible label.
  'duration',
  'height',
  'pattern',
  // A preview, an upload, a library button and a remove — four named controls,
  // and no single input for an `htmlFor` to point at.
  'media',
  // A `<fieldset>` whose `<legend>` IS the name. A `<Label htmlFor>` beside it
  // would point at the fieldset element, and would compete with the legend for
  // the same job.
  'object',
]);

/**
 * Whether the form should print a caption instead of a `<Label htmlFor>`.
 *
 * A `custom` field is the one kind the set cannot answer for — only its author
 * knows whether the render names itself — so it says so with `isSelfNaming`.
 */
export function namesItself(field: FieldDef): boolean {
  if (field.kind === 'custom') {
    return field.isSelfNaming === true;
  }

  return SELF_NAMING_KINDS.has(field.kind);
}
